import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Heart,
  Loader2,
  MapPin,
  Phone,
  PartyPopper,
  ShieldCheck,
  Sparkles,
  Star,
  Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FadeUp } from "@/components/anim";
import { ReviewsWall } from "@/components/ReviewsWall";
import { api } from "@/lib/api";
import { CONTACT, BOOKING_URL, TERM, SITE_URL } from "@/lib/site";

const HERO_IMG = "https://images.unsplash.com/photo-1518834107812-67b0b7c58434?w=1100&q=85";

const STYLE_OPTIONS = [
  "Not sure yet — help me choose",
  "Happy Feet (ages 2–4)",
  "Ballet",
  "Tap",
  "Modern",
  "Musical Theatre",
  "Street Dance",
  "Contemporary",
  "Audition Prep",
];

const TRUST = [
  { icon: Trophy, label: "100% ISTD exam pass record" },
  { icon: Heart, label: "SEND & neurodiverse dancers warmly welcome" },
  { icon: ShieldCheck, label: "17 years teaching in Whitstable" },
  { icon: Sparkles, label: "No experience needed — ages 2 to 18" },
];

const BENEFITS = [
  {
    icon: PartyPopper,
    title: "Fun comes first",
    text: "Kids learn fastest when they're grinning. Stand outside the studio for a minute — you'll hear the laughter through the door.",
  },
  {
    icon: Trophy,
    title: "Proper training",
    text: "We teach the respected ISTD syllabus with a 100% exam pass record. Real qualifications, taught patiently — never a certificate factory.",
  },
  {
    icon: Heart,
    title: "Every child included",
    text: "No auditions, no 'right body type'. We happily adapt pace and sensory load for SEND and neurodiverse dancers. Tell us what helps — that's what we'll do.",
  },
  {
    icon: Star,
    title: "Everyone performs",
    text: "When show time comes, every single dancer gets their moment on stage. Not just the front row — your child included.",
  },
];

const STEPS = [
  { n: "01", title: "Send your details", text: "Pop your name and your child's age in the form. Takes under a minute — no payment, no commitment." },
  { n: "02", title: "Kerry finds the right class", text: "We'll match your child to the perfect class for their age and interests, and text or email you a day and time." },
  { n: "03", title: "Come along free", text: "Turn up in comfy clothes for a no-pressure taster. If they love it (they will), we'll help you book the term." },
];

const FAQS = [
  {
    q: "Does my child need any dance experience?",
    a: "None at all. Most of our dancers started as complete beginners — including the ones now taking Intermediate exams. Just turn up in comfy clothes and we'll do the rest.",
  },
  {
    q: "How old does my child need to be?",
    a: "We teach from age 2 (our Happy Feet class) right up to 18. There's a class for every age and stage.",
  },
  {
    q: "Is the taster class really free?",
    a: "Yes. Your child's first taster class is completely free and with zero obligation. It's the best way to see if they love it before you commit to a term.",
  },
  {
    q: "My child has additional needs — will they be looked after?",
    a: "Yes, properly. We teach plenty of SEND and neurodiverse children and adapt pace, structure and sensory load to suit each dancer. Tell Kerry what helps your child feel comfortable and that's exactly what we'll do.",
  },
  {
    q: "Where are the classes held?",
    a: "At three friendly studios in the heart of Whitstable — The Umbrella Centre on Oxford Street, The Whitstable School on Bellevue Road, and the Whitstable Swimming Pool dance studio. All minutes from the sea, with parking and somewhere to wait.",
  },
];

const LandingLogo = () => (
  <a href="/" className="flex items-baseline gap-3 shrink-0" data-testid="landing-logo-link">
    <span className="font-display text-3xl sm:text-4xl leading-none text-foreground">
      KERRY<span className="text-[hsl(var(--kerrys-coral))]">&rsquo;S</span>
    </span>
    <span className="hidden sm:block text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">
      Whitstable
    </span>
  </a>
);

const LeadForm = ({ formRef }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    parent_name: "",
    child_age: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_name.trim()) {
      toast.error("Please add your name so Kerry knows who to reply to");
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Please leave an email or phone number so Kerry can get back to you");
      return;
    }
    setSubmitting(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const source = params.get("utm_source") || "landing-whitstable";
      await api.post("/leads", {
        ...form,
        source,
        utm: window.location.search.replace(/^\?/, ""),
        page_path: window.location.pathname,
      });
      // Redirect to a dedicated thank-you page — this gives Google Ads & Meta a clean
      // conversion URL / pageview to fire on.
      navigate("/kids-dance-classes-whitstable/thank-you", { state: { name: form.parent_name } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again or call Kerry.");
      setSubmitting(false);
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={submit}
      className="border-[0.5px] border-foreground bg-card p-6 sm:p-8"
      data-testid="landing-lead-form"
      id="taster-form"
    >
      <div className="inline-flex items-center gap-2 border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun))] px-3 py-1.5 -rotate-1 mb-5">
        <PartyPopper className="h-4 w-4" />
        <span className="font-display uppercase text-xl leading-none tracking-[0.02em]">Claim your free taster</span>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Leave your details and Kerry will find the perfect first class for your child. No payment, no pressure.
      </p>
      <div className="space-y-4">
        <div>
          <Label htmlFor="parent_name" className="text-xs font-bold uppercase tracking-[0.12em]">
            Your name
          </Label>
          <Input
            id="parent_name"
            value={form.parent_name}
            onChange={set("parent_name")}
            placeholder="e.g. Sarah Jones"
            className="rounded-none border-[0.5px] border-foreground mt-1.5 h-11 bg-background focus-visible:ring-[hsl(var(--kerrys-sea))]"
            data-testid="landing-input-name"
            required
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="child_age" className="text-xs font-bold uppercase tracking-[0.12em]">
              Child&rsquo;s age
            </Label>
            <Input
              id="child_age"
              value={form.child_age}
              onChange={set("child_age")}
              placeholder="e.g. 6"
              className="rounded-none border-[0.5px] border-foreground mt-1.5 h-11 bg-background focus-visible:ring-[hsl(var(--kerrys-sea))]"
              data-testid="landing-input-child-age"
            />
          </div>
          <div>
            <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-[0.12em]">
              Phone
            </Label>
            <Input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              placeholder="Mobile number"
              className="rounded-none border-[0.5px] border-foreground mt-1.5 h-11 bg-background focus-visible:ring-[hsl(var(--kerrys-sea))]"
              data-testid="landing-input-phone"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.12em]">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="you@example.com"
            className="rounded-none border-[0.5px] border-foreground mt-1.5 h-11 bg-background focus-visible:ring-[hsl(var(--kerrys-sea))]"
            data-testid="landing-input-email"
          />
        </div>
        <div>
          <Label htmlFor="interest" className="text-xs font-bold uppercase tracking-[0.12em]">
            Interested in
          </Label>
          <Select value={form.interest} onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}>
            <SelectTrigger
              id="interest"
              className="rounded-none border-[0.5px] border-foreground mt-1.5 h-11 bg-background focus:ring-[hsl(var(--kerrys-sea))]"
              data-testid="landing-select-interest"
            >
              <SelectValue placeholder="Pick a style (or 'not sure yet')" />
            </SelectTrigger>
            <SelectContent className="rounded-none border-[0.5px] border-foreground">
              {STYLE_OPTIONS.map((s) => (
                <SelectItem key={s} value={s} className="rounded-none">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="message" className="text-xs font-bold uppercase tracking-[0.12em]">
            Anything Kerry should know? <span className="text-muted-foreground font-medium normal-case tracking-normal">(optional)</span>
          </Label>
          <Textarea
            id="message"
            value={form.message}
            onChange={set("message")}
            placeholder="Preferred days, any additional needs, questions…"
            rows={3}
            className="rounded-none border-[0.5px] border-foreground mt-1.5 bg-background focus-visible:ring-[hsl(var(--kerrys-sea))]"
            data-testid="landing-input-message"
          />
        </div>
      </div>
      <Button
        type="submit"
        disabled={submitting}
        className="w-full mt-6 rounded-none bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-foreground font-bold uppercase tracking-[0.12em] text-sm h-12"
        data-testid="landing-lead-submit-button"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…
          </>
        ) : (
          <>
            Book my free taster <ArrowRight className="h-4 w-4 ml-2" />
          </>
        )}
      </Button>
      <p className="text-xs text-muted-foreground mt-3 text-center">
        We&rsquo;ll only use your details to arrange your taster. No spam, ever.
      </p>
    </form>
  );
};

export default function LandingKidsWhitstable() {
  const formRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToForm = () => {
    document.getElementById("taster-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>Free Taster · Kids Dance Classes in Whitstable | Kerry&rsquo;s</title>
        <meta
          name="description"
          content="Book a FREE first taster dance class for your child in Whitstable. Ballet, tap, modern, musical theatre & street dance for ages 2–18. 100% ISTD pass record, SEND-friendly, no experience needed."
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`${SITE_URL}/kids-dance-classes-whitstable`} />
        <meta property="og:title" content="Free first dance taster for kids in Whitstable — Kerry's" />
        <meta
          property="og:description"
          content="A fun, inclusive dance school for ages 2–18 in Whitstable. Claim your child's free taster class today."
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
      </Helmet>

      {/* Minimal distraction-free header */}
      <header
        className={`sticky top-0 z-50 bg-background border-b-[0.5px] border-foreground/30 transition-shadow ${
          scrolled ? "shadow-[0_1px_0_rgba(15,23,42,0.12)]" : ""
        }`}
        data-testid="landing-header"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-18 flex items-center justify-between gap-4 py-2">
          <LandingLogo />
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={CONTACT.phoneHref}
              className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-foreground hover:text-[hsl(var(--kerrys-sea))] transition-colors"
              data-testid="landing-header-phone"
            >
              <Phone className="h-4 w-4" /> {CONTACT.phone}
            </a>
            <Button
              onClick={scrollToForm}
              className="rounded-none bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-foreground font-bold uppercase tracking-[0.12em] text-xs px-4 sm:px-5 h-10"
              data-testid="landing-header-cta"
            >
              Free taster
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero + form */}
        <section className="grain border-b-[0.5px] border-foreground/30 overflow-hidden" data-testid="landing-hero">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-16 sm:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-10">
            <div className="lg:col-span-6 flex flex-col justify-center">
              <FadeUp>
                <div className="inline-flex items-center gap-2 border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun))] px-3 py-1.5 -rotate-1 w-fit">
                  <Sparkles className="h-4 w-4" />
                  <span className="font-display uppercase text-lg leading-none tracking-[0.03em]">
                    Whitstable &middot; ages 2&ndash;18
                  </span>
                </div>
              </FadeUp>
              <FadeUp delay={0.06}>
                <h1 className="font-display uppercase text-5xl sm:text-7xl lg:text-[5.4rem] leading-[0.88] mt-6 text-foreground">
                  Your child&rsquo;s first
                  <br />
                  dance class is
                  <br />
                  <span className="text-[hsl(var(--kerrys-coral))]">on us.</span>
                </h1>
              </FadeUp>
              <FadeUp delay={0.12}>
                <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
                  Kerry&rsquo;s is Whitstable&rsquo;s fun, inclusive dance school. Ballet, tap, modern, musical theatre and
                  street &mdash; taught with kindness, proper technique and a 100% ISTD exam pass record. Claim your
                  child&rsquo;s <strong className="text-foreground">free taster class</strong> and watch their confidence take off.
                </p>
              </FadeUp>
              <FadeUp delay={0.18}>
                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {TRUST.map((t) => (
                    <li key={t.label} className="flex items-start gap-2.5">
                      <t.icon className="h-5 w-5 shrink-0 text-[hsl(var(--kerrys-sea))] mt-0.5" />
                      <span className="text-sm font-medium text-foreground leading-snug">{t.label}</span>
                    </li>
                  ))}
                </ul>
              </FadeUp>
              <FadeUp delay={0.22}>
                <div className="relative mt-10 hidden lg:block">
                  <img
                    src={HERO_IMG}
                    alt="Happy children dancing together in a bright studio"
                    className="w-full h-64 object-cover border-[0.5px] border-foreground"
                    loading="eager"
                  />
                  <div className="absolute -bottom-5 -right-4 bg-foreground text-background px-5 py-4 -rotate-2 border-[0.5px] border-background/20">
                    <p className="font-display text-4xl leading-none">100%</p>
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] mt-1 text-background/80">ISTD pass record</p>
                  </div>
                </div>
              </FadeUp>
            </div>
            <div className="lg:col-span-6 flex items-start">
              <FadeUp delay={0.1} className="w-full">
                <LeadForm formRef={formRef} />
              </FadeUp>
            </div>
          </div>
        </section>

        {/* Trust marquee */}
        <div className="bg-[hsl(var(--kerrys-sun))] border-b-[0.5px] border-foreground/60 overflow-hidden py-3.5 grain" data-testid="landing-marquee">
          <div className="marquee-track flex w-max items-center gap-8 pr-8">
            {[...Array(3)].flatMap((_, r) =>
              ["Confidence", "Kindness", "Proper technique", "Every child included", "Fun first", "By the sea"].map(
                (label, i) => (
                  <span key={`${r}-${i}`} className="flex items-center gap-8 shrink-0">
                    <span className="font-display uppercase text-3xl sm:text-4xl leading-none text-foreground">{label}</span>
                    <Star className="h-3.5 w-3.5 text-foreground" fill="currentColor" />
                  </span>
                )
              )
            )}
          </div>
        </div>

        {/* Benefits */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24" data-testid="landing-benefits">
          <FadeUp className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[hsl(var(--kerrys-sea))] mb-4">Why parents choose Kerry&rsquo;s</p>
            <h2 className="font-display uppercase text-5xl sm:text-6xl leading-[0.92]">
              A dance school parents actually trust
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-14">
            {BENEFITS.map((b, i) => (
              <FadeUp key={b.title} delay={i * 0.06}>
                <div className="border-[0.5px] border-foreground bg-card p-7 sm:p-8 h-full" data-testid="landing-benefit-card">
                  <span className="h-12 w-12 border-[0.5px] border-foreground bg-[hsl(var(--kerrys-sun-pale))] flex items-center justify-center">
                    <b.icon className="h-6 w-6 text-foreground" />
                  </span>
                  <h3 className="font-display uppercase text-3xl sm:text-4xl leading-[0.92] mt-5">{b.title}</h3>
                  <p className="text-muted-foreground mt-3 leading-relaxed">{b.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        {/* How the taster works */}
        <section className="bg-[hsl(var(--kerrys-ink))] text-background grain" data-testid="landing-steps">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
            <FadeUp className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[hsl(var(--kerrys-sun))] mb-4">Easy as 1-2-3</p>
              <h2 className="font-display uppercase text-5xl sm:text-6xl leading-[0.92] text-background">
                How your free taster works
              </h2>
            </FadeUp>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-10 mt-16">
              {STEPS.map((s, i) => (
                <FadeUp key={s.n} delay={i * 0.08}>
                  <div className="border-t-[0.5px] border-background/40 pt-6" data-testid="landing-step-card">
                    <p className="font-display text-2xl text-[hsl(var(--kerrys-coral))]">{s.n}</p>
                    <h3 className="font-display uppercase text-4xl leading-[0.92] mt-3">{s.title}</h3>
                    <p className="text-background/70 mt-4 leading-relaxed">{s.text}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.1}>
              <div className="mt-14">
                <Button
                  onClick={scrollToForm}
                  size="lg"
                  className="rounded-none bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-background/20 font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                  data-testid="landing-steps-cta"
                >
                  Claim your free taster <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </FadeUp>
          </div>
        </section>

        {/* Locations */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24" data-testid="landing-locations">
          <FadeUp className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[hsl(var(--kerrys-sea))] mb-4">Right on your doorstep</p>
            <h2 className="font-display uppercase text-5xl sm:text-6xl leading-[0.92]">Three studios by the sea</h2>
            <p className="mt-5 text-muted-foreground text-base sm:text-lg leading-relaxed">
              Weekly classes run at three friendly Whitstable venues — all minutes from the seafront, with parking and
              somewhere warm to wait.
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-12">
            {[
              { name: "The Umbrella Centre", area: "Oxford Street", note: "Mondays & Tuesdays · town centre" },
              { name: "The Whitstable School", area: "Bellevue Road", note: "Monday & Wednesday evenings · sprung floor" },
              { name: "Whitstable Swimming Pool", area: "Tower Parade", note: "Fridays · purpose-built studio" },
            ].map((v, i) => (
              <FadeUp key={v.name} delay={i * 0.06}>
                <div className="border-[0.5px] border-foreground bg-card p-6 h-full" data-testid="landing-location-card">
                  <MapPin className="h-5 w-5 text-[hsl(var(--kerrys-coral))]" />
                  <h3 className="font-display uppercase text-3xl leading-[0.95] mt-3">{v.name}</h3>
                  <p className="text-sm font-bold uppercase tracking-[0.12em] text-muted-foreground mt-2">{v.area}</p>
                  <p className="text-sm text-muted-foreground mt-3">{v.note}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t-[0.5px] border-foreground/40 bg-secondary grain" data-testid="landing-faq">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
            <FadeUp>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[hsl(var(--kerrys-sea))] mb-4">Good to know</p>
              <h2 className="font-display uppercase text-5xl sm:text-6xl leading-[0.92] mb-10">Questions parents ask</h2>
            </FadeUp>
            <Accordion type="single" collapsible className="border-t-[0.5px] border-foreground">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b-[0.5px] border-foreground" data-testid="landing-faq-item">
                  <AccordionTrigger className="text-left font-bold text-base sm:text-lg hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Reviews — social proof (renders only when Kerry has added reviews) */}
        <ReviewsWall variant="dark" eyebrow="Real parents, real reviews" title="Families love Kerry's" max={6} />

        {/* Final CTA */}
        <section className="bg-[hsl(var(--kerrys-coral))] border-t-[0.5px] border-foreground grain" data-testid="landing-final-cta">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 text-center sm:text-left">
            <FadeUp>
              <h2 className="font-display uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.88] text-foreground">
                Come and dance with us
              </h2>
              <p className="mt-6 text-foreground/80 text-lg max-w-xl leading-relaxed mx-auto sm:mx-0">
                First taster is free and no-pressure. Booking is open now — {TERM.label.toLowerCase()} runs {TERM.start} to{" "}
                {TERM.end}.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-4 justify-center sm:justify-start">
                <Button
                  onClick={scrollToForm}
                  size="lg"
                  className="rounded-none bg-foreground text-background hover:bg-foreground/90 font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                  data-testid="landing-final-form-button"
                >
                  Claim your free taster <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                  data-testid="landing-final-book-button"
                >
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                    Book online now
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-none bg-transparent border-[0.5px] border-foreground text-foreground hover:bg-foreground hover:text-background font-bold uppercase tracking-[0.12em] text-sm px-8 h-12"
                  data-testid="landing-final-call-button"
                >
                  <a href={CONTACT.phoneHref}>
                    <Phone className="h-4 w-4 mr-2" /> Call Kerry
                  </a>
                </Button>
              </div>
            </FadeUp>
          </div>
        </section>
      </main>

      {/* Minimal footer */}
      <footer className="bg-[hsl(var(--kerrys-ink))] text-background py-10" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-display text-2xl">
            Kerry<span className="text-[hsl(var(--kerrys-coral))]">&rsquo;s</span>{" "}
            <span className="text-background/50 text-sm align-middle">&middot; Whitstable, Kent</span>
          </p>
          <div className="flex items-center gap-6 text-sm text-background/70">
            <a href={CONTACT.phoneHref} className="hover:text-background transition-colors">
              {CONTACT.phone}
            </a>
            <a href={CONTACT.emailHref} className="hover:text-background transition-colors">
              {CONTACT.email}
            </a>
            <a href="/" className="hover:text-background transition-colors">
              Full site
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
